import { SelectOptionType } from '@octano/global-ui';
import { SemesterCourse } from '../../components/curriculum/types';
import {
  CourseValidationSubType,
  CourseValidationType,
} from '../../types/CourseValidationType';
import { Institution } from '../Configurations/Institutions/interfaces/type';
import {
  CourseEnrollment,
  CreateValidationFields,
  CreateValidationSubmitValues,
} from './types';

export const convertValidationToSubmit = (
  validation?: SemesterCourse['validation'],
): CreateValidationSubmitValues | undefined => {
  if (!validation) {
    return undefined;
  }

  const values: CreateValidationSubmitValues = {
    validationType: validation.validationType,
    validationSupplementaryFilesIds: validation.supplementaryFiles || [],
  };

  if (values.validationType === CourseValidationType.RAP) {
    values.rap = {
      validationSubType:
        validation?.validationSubType || CourseValidationSubType.Validation,
      courseGrade: validation?.finalGrade || '',
      examGrade: validation?.examGrade || '',
      description: validation?.description || '',
    };
  }
  if (values.validationType === CourseValidationType.External) {
    values.external = {
      institutionId: validation?.institutionId || 0,
      courseGrade: validation?.finalGrade || '',
      externalGrade: validation?.externalGrade || '',
      externalCourseName: validation?.externalCourseName || '',
      description: validation?.description || '',
    };
  }
  if (values.validationType === CourseValidationType.Internal) {
    values.internal = {
      courseEnrollmentId: validation?.courseEnrollmentId,
    };
  }

  return values;
};

export const convertSubmitToFields = ({
  submitValues,
  validationSubTypeOptions,
  validationTypeOptions,
  courseEnrollments,
  institutions,
}: {
  submitValues: CreateValidationSubmitValues | undefined;
  validationTypeOptions: SelectOptionType[];
  validationSubTypeOptions: SelectOptionType[];
  courseEnrollments: CourseEnrollment[];
  institutions: Institution[];
}): Partial<CreateValidationFields> | undefined => {
  if (!submitValues) {
    return undefined;
  }

  const courseEnrollmentOptions: SelectOptionType[] = courseEnrollments.map(
    (e) => ({
      label: e.course?.name || '',
      value: e.id,
      courseEnrollment: e,
    }),
  );
  const institutionsOptions: SelectOptionType[] = institutions.map((e) => ({
    label: e.name,
    value: e.id,
  }));

  const values: Partial<CreateValidationFields> = {
    validationType:
      validationTypeOptions?.find(
        (e) => e.value === submitValues.validationType,
      ) || null,
    validationSupplementaryFilesIds:
      submitValues.validationSupplementaryFilesIds || [],
  };

  if (submitValues.validationType === CourseValidationType.RAP) {
    values.rap = {
      validationSubType:
        validationSubTypeOptions?.find(
          (e) => e.value === submitValues?.rap?.validationSubType,
        ) || null,
      courseGrade: submitValues?.rap?.courseGrade || '',
      examGrade: submitValues?.rap?.examGrade || '',
      description: submitValues?.rap?.description || '',
    };
  }
  if (submitValues.validationType === CourseValidationType.Internal) {
    values.internal = {
      courseEnrollmentId:
        courseEnrollmentOptions?.find(
          (e) => e.value === submitValues?.internal?.courseEnrollmentId,
        ) || null,
    };
  }
  if (submitValues.validationType === CourseValidationType.External) {
    values.external = {
      institutionId:
        institutionsOptions?.find(
          (e) => e.value === submitValues?.external?.institutionId,
        ) || null,
      courseGrade: submitValues?.external?.courseGrade || '',
      externalGrade: submitValues?.external?.externalGrade || '',
      externalCourseName: submitValues?.external?.externalCourseName || '',
      description: submitValues?.external?.description || '',
    };
  }

  return values;
};

export const convertFieldsToSubmit = (
  values: CreateValidationFields,
): CreateValidationSubmitValues => {
  const payload: CreateValidationSubmitValues = {
    ...values,
    validationType: values?.validationType?.value as CourseValidationType,
    rap: undefined,
    internal: undefined,
    external: undefined,
  };

  // Handle RAP
  if (payload?.validationType !== CourseValidationType.RAP) {
    delete payload.rap;
  } else if (values.rap) {
    payload.rap = {
      ...values.rap,
      validationSubType: values.rap.validationSubType
        ?.value as CourseValidationSubType,
    };
  }

  // Handle External
  if (payload?.validationType !== CourseValidationType.External) {
    delete payload.external;
  } else if (values.external) {
    payload.external = {
      ...values.external,
      institutionId: values.external.institutionId?.value as number,
    };
  }

  // Handle Internal
  if (payload?.validationType !== CourseValidationType.Internal) {
    delete payload.internal;
  } else if (values.internal) {
    payload.internal = {
      ...values.internal,
      courseEnrollmentId: values.internal.courseEnrollmentId?.value as number,
    };
  }
  return payload;
};
